/**
 * A single series over time with a faint area under it, for a hero panel.
 *
 * This is the "what has margin been doing" glance beside a headline figure,
 * not the month-by-month chart you click into (that is MarginTrend). So: no
 * title, no footer, no click, no outlier clamping. Its eyebrow is the hero's.
 *
 * The benchmark is a reference line labelled at its own height, never a second
 * series. Gaps break the line AND the area: a filled region across a month
 * with no revenue would assert a trend that was never measured.
 */
import { useId } from "react";
import { BENCHMARK, GRID, niceBounds, type Point, SERIES_1 } from "./chart-primitives";

const W = 520;
const PAD = { top: 10, right: 14, bottom: 22, left: 30 };

export function TrendArea({
  points,
  benchmark,
  label = "Margin by month",
  height = 118,
}: {
  points: Point[];
  benchmark: number | null;
  label?: string;
  height?: number;
}) {
  const gradId = useId();
  const present = points.filter((p): p is Point & { value: number } => p.value !== null);
  const last = present[present.length - 1];
  if (present.length < 2 || !last) return null;

  const values = present.map((p) => p.value);
  const [lo, hi] = niceBounds(benchmark === null ? values : [...values, benchmark], false);
  const innerW = W - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (i / (points.length - 1)) * innerW;
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo)) * innerH;
  const baseY = PAD.top + innerH;

  const runs: { i: number; v: number }[][] = [];
  let run: { i: number; v: number }[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (run.length > 1) runs.push(run);
      run = [];
    } else {
      run.push({ i, v: p.value });
    }
  });
  if (run.length > 1) runs.push(run);

  const tickCount = 3;
  const ticks = Array.from({ length: tickCount + 1 }, (_, k) => lo + ((hi - lo) * k) / tickCount);
  const lastIndex = points.indexOf(last);
  const showBench = benchmark !== null && benchmark >= lo && benchmark <= hi;

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      className="trend-area w-full max-w-[600px]"
      role="img"
      aria-label={`${label}. ${present.length} periods.`}
    >
      <title>{label}</title>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={SERIES_1} stopOpacity={0.28} />
          <stop offset="1" stopColor={SERIES_1} stopOpacity={0} />
        </linearGradient>
      </defs>

      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke={GRID} />
          <text
            x={PAD.left - 6}
            y={y(t) + 3.5}
            textAnchor="end"
            className="fill-muted-foreground"
            style={{ fontSize: 10.5 }}
          >
            {Math.round(t)}%
          </text>
        </g>
      ))}

      {showBench ? (
        <>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(benchmark)}
            y2={y(benchmark)}
            stroke={BENCHMARK}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
          <text
            data-benchmark-label
            x={W - PAD.right}
            y={y(benchmark) - 5}
            textAnchor="end"
            className="fill-muted-foreground"
            style={{ fontSize: 10.5 }}
          >
            benchmark {benchmark}%
          </text>
        </>
      ) : null}

      {runs.map((r) => {
        const line = r.map((s) => `${x(s.i)},${y(s.v)}`).join(" ");
        const first = r[0];
        const end = r[r.length - 1];
        if (!first || !end) return null;
        const area = `M${x(first.i)},${baseY} L${line.replace(/ /g, " L")} L${x(end.i)},${baseY} Z`;
        return (
          <g key={`run-${first.i}`}>
            <path data-area d={area} fill={`url(#${gradId})`} />
            <polyline
              points={line}
              fill="none"
              stroke={SERIES_1}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </g>
        );
      })}

      <circle
        data-trend-end
        cx={x(lastIndex)}
        cy={y(last.value)}
        r={4}
        fill={SERIES_1}
        stroke="var(--card)"
        strokeWidth={2}
      />

      {points.map((p, i) =>
        i % 2 === 0 ? (
          <text
            key={`lbl-${p.label}`}
            x={x(i)}
            y={height - 6}
            textAnchor="middle"
            className="fill-muted-foreground"
            style={{ fontSize: 10.5 }}
          >
            {p.label.replace(/ \d{4}$/, "")}
          </text>
        ) : null,
      )}
    </svg>
  );
}
